import {
  QuarterlyRepeatRule,
  QuarterType,
} from '../../../lib/shared/calendar.types'

interface QuarterlyRecurrenceProps {
  rule: QuarterlyRepeatRule
  onChange: (rule: QuarterlyRepeatRule) => void
  accountingReferenceDate?: string // MM-DD format for accounting year end
}

const MONTHS_IN_QUARTER = [
  { value: 1, label: 'First Month' },
  { value: 2, label: 'Second Month' },
  { value: 3, label: 'Third Month' },
]

const QUARTER_TYPES = [
  {
    value: 'calendar' as QuarterType,
    label: 'Calendar Year - Jan to Dec',
  },
  {
    value: 'tax' as QuarterType,
    label: 'Tax Year - Apr to Mar',
  },
  {
    value: 'accounting' as QuarterType,
    label: 'Accounting Year',
    description: 'Based on your business year start date',
  },
]

function getQuarterMonths(
  quarterType: QuarterType,
  monthInQuarter: number,
  accountingReferenceDate?: string,
): string {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  let startMonth: number
  switch (quarterType) {
    case 'calendar':
      startMonth = 0 // January
      break
    case 'tax':
      startMonth = 3 // April
      break
    case 'accounting':
      if (!accountingReferenceDate) return 'Set business accounting dates'
      const date = new Date(accountingReferenceDate)
      const endMonth = date.getMonth()
      startMonth = (endMonth + 1) % 12
      break
  }

  const quarters = Array.from({ length: 4 }, (_, i) => {
    const quarterStart = (startMonth + i * 3) % 12
    return monthNames[(quarterStart + monthInQuarter - 1) % 12]
  })

  return `Q1: ${quarters[0]}, Q2: ${quarters[1]}, Q3: ${quarters[2]}, Q4: ${quarters[3]}`
}

export function QuarterlyRecurrence({
  rule,
  onChange,
  accountingReferenceDate,
}: QuarterlyRecurrenceProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Quarter type
        </label>
        <select
          value={rule.quarterType ?? ''}
          onChange={(e) =>
            onChange({
              ...rule,
              quarterType: e.target.value as QuarterType,
              month: 1,
            })
          }
          className="mt-1 block w-full rounded-md border border-grey-500 p-2 focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="">--- Select a quarter type ---</option>
          {QUARTER_TYPES.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-sm text-gray-500">
          {
            QUARTER_TYPES.find((qt) => qt.value === rule.quarterType)
              ?.description
          }
        </p>
      </div>

      {!!rule.quarterType && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Month in Quarter
            </label>
            <select
              value={rule.month}
              onChange={(e) =>
                onChange({
                  ...rule,
                  month: parseInt(e.target.value) as 1 | 2 | 3,
                })
              }
              className="mt-1 block w-full rounded-md border border-grey-500 p-2 focus:border-blue-500 focus:ring-blue-500"
            >
              {MONTHS_IN_QUARTER.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label} (
                  {getQuarterMonths(
                    rule.quarterType,
                    value,
                    accountingReferenceDate,
                  )}
                  )
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Day of Month
            </label>
            <select
              value={rule.value}
              onChange={(e) =>
                onChange({
                  ...rule,
                  type: 'dayOfMonth',
                  value: parseInt(e.target.value),
                })
              }
              className="mt-1 block w-full rounded-md border border-grey-500 p-2 focus:border-blue-500 focus:ring-blue-500"
            >
              {[...Array(31)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
            {rule.value >= 29 && (
              <p className="mt-1 text-sm text-amber-600">
                {rule.value === 31
                  ? 'For months with fewer than 31 days, the event will occur on the last day of the month (28th-30th).'
                  : rule.value === 30
                    ? 'For months with fewer than 30 days, the event will occur on the last day of the month (28th/29th).'
                    : 'For February with 28 days, the event will occur on the 28th (or 29th in leap years).'}
              </p>
            )}
          </div>
        </>
      )}

      {rule.quarterType === 'accounting' && !accountingReferenceDate && (
        <p className="text-sm text-amber-600">
          Please set your business accounting year dates in business settings to
          use accounting year quarters
        </p>
      )}
    </div>
  )
}
